import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { AlertTriangle, ArrowRight, Pencil } from "lucide-react";
import { useEffect, useState } from "react";
import { CLAY, WARM } from "@shared/brand";

export interface ConfirmExtractionField {
  fieldId: string;
  name: string;
  value: string;
  /** DB-45 1H: server-computed current USER value this write would overwrite. Present
   *  ONLY when the write replaces different, non-seeded user data — so its presence is
   *  the authoritative "this is destructive" signal. Blanks / seeded scaffolds /
   *  same-value writes never carry it. */
  replacesValue?: string;
  /** s168: the current value this write will change, when the merge rule does NOT flag it
   *  — i.e. seeded-scaffold provenance, which DB-45 defines as safe to overwrite. Purely
   *  informational: it carries NO gate and NO checkbox, and the field is written exactly
   *  as it would be without it. It exists so the planner can see what changes before
   *  pressing Confirm & Save, which was previously invisible for scaffold values. */
  currentValue?: string;
}

interface ConfirmExtractionModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Editable extracted fields. Host owns the array; this modal is controlled. */
  fields: ConfirmExtractionField[];
  /** Called when the user edits a field value (by index into `fields`). */
  onFieldChange: (index: number, value: string) => void;
  saving: boolean;
  /** Receives ONLY the fields to write: every non-destructive field, plus the
   *  destructive (replacesValue) fields the planner explicitly opted to replace. */
  onConfirm: (fieldsToWrite: ConfirmExtractionField[]) => void;
  onDiscard: () => void;
}

/** A field is a destructive overwrite when the server flagged a replacesValue AND the
 *  (possibly edited) value still differs from it. Editing the value back to the current
 *  value disarms the gate. */
function isDestructive(f: ConfirmExtractionField): boolean {
  return !!f.replacesValue && f.replacesValue !== f.value;
}

/**
 * Review-and-confirm dialog for AI-extracted document fields. Controlled +
 * presentational. DB-45 1H: any field that would OVERWRITE existing user data
 * (server-flagged `replacesValue`) is surfaced in clay, shows "Replaces: …", and is
 * EXCLUDED by default — the planner must opt in per field to replace it. Blank-fills
 * and same-value writes stay included by default (no friction). On confirm, only the
 * included fields are written.
 *
 * Single source of truth for the confirm UX so every upload surface stays one
 * experience (s76 — factored out of GenericCategoryPage's inline dialog).
 */
export function ConfirmExtractionModal({
  open,
  onOpenChange,
  fields,
  onFieldChange,
  saving,
  onConfirm,
  onDiscard,
}: ConfirmExtractionModalProps) {
  // Per-field opt-in for destructive overwrites, keyed by fieldId. Default empty =
  // every destructive field excluded. Reset when the field SET changes (a new pending),
  // not on every value edit (which would clear the planner's acknowledgements).
  const [replaceOptIn, setReplaceOptIn] = useState<Set<string>>(new Set());
  const fieldIdKey = fields.map((f) => f.fieldId).join("|");
  useEffect(() => {
    setReplaceOptIn(new Set());
  }, [fieldIdKey]);

  const toggleOptIn = (fieldId: string) =>
    setReplaceOptIn((prev) => {
      const next = new Set(prev);
      next.has(fieldId) ? next.delete(fieldId) : next.add(fieldId);
      return next;
    });

  const destructiveCount = fields.filter(isDestructive).length;

  const handleConfirm = () => {
    // Write every non-destructive field, plus the destructive fields opted in.
    const toWrite = fields.filter((f) => !isDestructive(f) || replaceOptIn.has(f.fieldId));
    onConfirm(toWrite);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Review Extracted Fields</DialogTitle>
          {/* s97 Fix 4: nudge the planner that values are editable — bump the subtitle
              from muted to taupe + medium weight. */}
          <DialogDescription className="font-medium" style={{ color: WARM.taupe }}>
            Edit any values before saving to your estate plan.
          </DialogDescription>
        </DialogHeader>

        {destructiveCount > 0 && (
          <div
            className="flex items-start gap-2 rounded-md border px-3 py-2 text-[13px]"
            style={{ backgroundColor: CLAY[100], borderColor: CLAY[400], color: CLAY[800] }}
            data-testid="confirm-overwrite-warning"
          >
            <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" style={{ color: CLAY[600] }} aria-hidden="true" />
            <span>
              {destructiveCount} field{destructiveCount === 1 ? "" : "s"} would replace information you
              already entered. Those are unchecked below — tick the ones you want to replace.
            </span>
          </div>
        )}

        <div className="space-y-3 max-h-96 overflow-y-auto">
          {fields.map((f, idx) => {
            const destructive = isDestructive(f);
            const optedIn = replaceOptIn.has(f.fieldId);
            return (
              <div
                key={f.fieldId}
                className="space-y-1 rounded-md"
                style={destructive ? { backgroundColor: CLAY[100], padding: "8px", border: `1px solid ${CLAY[400]}` } : undefined}
              >
                <div className="flex items-center gap-3">
                  <label className="text-sm text-muted-foreground w-36 shrink-0">{f.name}</label>
                  {/* s97 Fix 4: visible border + focus ring + a muted pencil affordance so
                      the input clearly reads as editable. */}
                  <div className="relative flex-1">
                    <input
                      className="w-full border border-warm-stone rounded px-3 py-1.5 pr-8 text-sm focus:outline-none focus:ring-1 focus:ring-sage-600/40 focus:border-sage-600"
                      value={f.value}
                      onChange={(e) => onFieldChange(idx, e.target.value)}
                      data-testid={`input-confirm-extraction-${f.fieldId}`}
                    />
                    <Pencil
                      className="absolute right-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 pointer-events-none"
                      style={{ color: WARM.taupe }}
                      aria-hidden="true"
                    />
                  </div>
                </div>
                {/* s168: neutral disclosure for an unflagged change (scaffold provenance).
                    Muted, no checkbox, no gate — it informs, it does not ask. Rendered only
                    when the edited value still differs, so editing back to the current value
                    makes the line disappear, matching how the destructive gate disarms. */}
                {!destructive && f.currentValue && f.currentValue !== f.value && (
                  <div className="pl-[9.75rem]">
                    <p className="text-[12px] text-muted-foreground" data-testid={`currently-${f.fieldId}`}>
                      Currently: {f.currentValue}
                    </p>
                  </div>
                )}
                {destructive && (
                  <div className="pl-[9.75rem] space-y-1">
                    <p className="text-[12px]" style={{ color: CLAY[800] }} data-testid={`replaces-${f.fieldId}`}>
                      Replaces: {f.replacesValue}
                    </p>
                    <label className="flex items-center gap-2 text-[12px] font-medium" style={{ color: CLAY[800] }}>
                      <input
                        type="checkbox"
                        className="h-3.5 w-3.5 accent-clay-600"
                        checked={optedIn}
                        onChange={() => toggleOptIn(f.fieldId)}
                        data-testid={`optin-replace-${f.fieldId}`}
                      />
                      Replace my existing value
                    </label>
                  </div>
                )}
              </div>
            );
          })}
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onDiscard} data-testid="button-discard-extraction">
            Discard
          </Button>
          <Button disabled={saving} onClick={handleConfirm} data-testid="button-confirm-extraction">
            {saving ? "Saving…" : <>Confirm &amp; Save<ArrowRight className="h-4 w-4" aria-hidden="true" /></>}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
